import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserPlus, LogIn } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";

import { NavItem } from "./navigation/types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChatInterface } from "@/contexts/ChatInterfaceContext";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * MAIN NAVIGATION COMPONENT
 * 
 * Handles the primary navigation bar that adapts based on user state and page context.
 * 
 * KEY FEATURES:
 * 1. Context-Aware Navigation: Different items based on auth state and current page
 * 2. Responsive Design: Mobile hamburger menu with overlay behavior
 * 3. Sidebar Integration: Can toggle dashboard sidebar on app pages
 * 4. Marketing vs App: Different navigation patterns for different page types
 * 
 * NAVIGATION PATTERNS:
 * - Marketing Pages: Product, Pricing, About (public-facing)
 * - App Pages: Dashboard, Posits, Analytics (authenticated users)
 * - Chat Pages: Minimal navigation to focus on conversation
 * 
 * KNOWN ISSUES (Pre-Launch):
 * - Inconsistent behavior between authenticated/unauthenticated states
 * - Mobile vs desktop navigation parity problems  
 * - Anchor links break when navigating from dashboard pages
 * - Logo click destination varies based on auth state
 * 
 * SIDEBAR INTEGRATION:
 * For dashboard pages, the hamburger menu toggles the sidebar instead of
 * showing a traditional mobile menu. This provides consistent navigation
 * across mobile and desktop experiences.
 */

const Navigation = () => {
  const [isMarketingMenuOpen, setIsMarketingMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const { toggleSidebar } = useChatInterface();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Safe sidebar toggle that handles context availability
  const safeSidebarToggle = () => {
    // For dashboard pages, use the ChatInterface context
    if (isDashboardPage) {
      try {
        toggleSidebar();
      } catch (error) {
        // If sidebar context is not available, try to dispatch a custom event
        // that the dashboard layout can listen to
        window.dispatchEvent(new CustomEvent('toggle-sidebar'));
      }
    } else {
      // For other pages that might use UI sidebar components,
      // dispatch a general sidebar toggle event
      window.dispatchEvent(new CustomEvent('toggle-sidebar'));
    }
  };

  const { data: posit } = useQuery({
    queryKey: ["user-posit-nav"],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("posits")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  const { data: isAdmin } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .rpc('has_role', { 
          user_id: user.id,
          role: 'admin'
        });
      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  // Determine if we're on marketing pages vs dashboard pages
  const isMarketingPage = location.pathname === '/' || 
                         location.pathname.startsWith('/#') ||
                         location.pathname === '/pricing';

  // Determine if we're on dashboard pages (where sidebar navigation is primary)
  const isDashboardPage = location.pathname.startsWith('/dashboard') ||
                         location.pathname.startsWith('/admin') ||
                         location.pathname === '/chat-history' ||
                         location.pathname === '/creator-dashboard' ||
                         location.pathname.startsWith('/chat/') ||
                         location.pathname.startsWith('/update-posit/') ||
                         location.pathname.startsWith('/p/') ||
                         location.pathname.startsWith('/public-posits') ||
                         location.pathname.startsWith('/bug-reports') ||
                         location.pathname.startsWith('/feature-requests') ||
                         location.pathname.startsWith('/prompt-tester') ||
                         location.pathname.startsWith('/ai-testing-dashboard');

  const toggleMarketingMenu = () => setIsMarketingMenuOpen(!isMarketingMenuOpen);

  // Context-aware hamburger handler
  const handleMobileMenuToggle = () => {
    if (isMarketingPage) {
      // Marketing pages: toggle simple mobile menu
      toggleMarketingMenu();
    } else {
      // Dashboard pages (both authenticated and unauthenticated): toggle sidebar
      safeSidebarToggle();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      if (location.hash) {
        setTimeout(() => {
          const element = document.querySelector(location.hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    scrollToHash();
  }, [location]);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    
    // Ensure hash starts with #
    const cleanHash = hash.startsWith('#') ? hash : `#${hash}`;
    
    if (location.pathname !== "/") {
      // If not on home page, navigate to home with hash
      navigate('/' + cleanHash);
    } else {
      // If already on home page, just scroll
      const element = document.querySelector(cleanHash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        // Update URL hash without full navigation
        window.history.pushState({}, "", cleanHash);
      }
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // Always go to home page for consistent behavior
    navigate('/');
  };

  // Show marketing navigation when on marketing pages, regardless of login status
  // Show dashboard navigation only when in dashboard areas
  // Remove Dashboard from regular nav items since it will be a separate button
  const navItems: NavItem[] = isMarketingPage
    ? [
        { label: "Product", path: "#product", isAnchor: true },
        { label: "Pricing", path: "#pricing", isAnchor: true },
      ]
    : user
    ? [
        { label: "Posits", path: "/dashboard/posits" },
      ]
    : [
        { label: "Product", path: "#product", isAnchor: true },
        { label: "Pricing", path: "#pricing", isAnchor: true },
      ];

  // Desktop navigation items (same as navItems for now)
  const desktopNavItems: NavItem[] = navItems;

  // Marketing mobile menu items
  const marketingMobileItems: NavItem[] = [
    { label: "Product", path: "#product", isAnchor: true },
    { label: "Pricing", path: "#pricing", isAnchor: true },
    // Only show Sign In/Sign Up if user is NOT logged in
    ...(user ? [] : [
      { label: "Sign In", path: "/login", icon: LogIn },
      { label: "Sign Up", path: "/signup", icon: UserPlus },
    ]),
  ];

  // Hide entire navigation on desktop dashboard pages (sidebar provides all navigation)
  if (isDashboardPage && !isMobile) {
    return null;
  }

  return (
    <header className={`sticky top-0 z-50 w-full transition-[background-color,backdrop-filter] duration-\[var(--duration-normal)\] ease-\[var(--ease-standard)\] ${isScrolled ? 'bg-background/90 backdrop-blur-sm' : ''}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <a
              href="/"
              onClick={handleLogoClick}
              className="flex items-center space-x-2"
            >
              <span className="text-2xl font-bold text-primary">Posit</span>
            </a>


          </div>

          <div className="flex items-center space-x-4">
            {/* Desktop navigation items - Product and Pricing */}
            {!isDashboardPage && (
              <nav className="hidden md:flex md:items-center md:space-x-6">
                {desktopNavItems.map((item) =>
                  item.isAnchor ? (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={(e) => handleAnchorClick(e, item.path)}
                      className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </nav>
            )}

            {/* Desktop Dashboard Button - Top Right when user is logged in */}
            {user && !isMobile && (
              <Link to="/dashboard">
                <Button variant="default" className="text-sm font-medium">
                  Dashboard
                </Button>
              </Link>
            )}

            {/* Desktop auth buttons - only show when not logged in */}
            {!user && (
              <div className="hidden md:flex md:items-center md:space-x-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-sm font-medium">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="default" className="text-sm font-medium">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Dashboard Button - Left side next to hamburger when user is logged in */}
            {user && isMobile && isMarketingPage && (
              <Link to="/dashboard">
                <Button variant="default" size="sm" className="text-sm font-medium">
                  Dashboard
                </Button>
              </Link>
            )}

            {/* Mobile Sign Up button - show only when not logged in and on marketing pages */}
            {!user && isMarketingPage && (
              <div className="md:hidden">
                <Link to="/signup">
                  <Button variant="default" size="sm" className="text-sm font-medium">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile hamburger menu - RIGHT side, show on marketing pages */}
            {isMarketingPage && (
              <button
                onClick={handleMobileMenuToggle}
                className="flex items-center justify-center w-11 h-11 rounded-[var(--radius)] text-foreground md:hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary z-50 relative bg-background border hover:bg-muted transition-colors"
              >
                <span className="sr-only">Open main menu</span>
                {isMarketingMenuOpen ? (
                  <X className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Menu className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}

            {/* Mobile Sign Up button - show only when not logged in and on dashboard pages */}
            {!user && !isMarketingPage && (
              <div className="md:hidden">
                <Link to="/signup">
                  <Button variant="default" size="sm" className="text-sm font-medium">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile hamburger menu for dashboard pages - toggles sidebar */}
            {!isMarketingPage && (
              <button
                onClick={handleMobileMenuToggle}
                className="flex items-center justify-center w-11 h-11 rounded-[var(--radius)] text-foreground md:hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary z-50 relative bg-background border hover:bg-muted transition-colors touch-manipulation"
              >
                <span className="sr-only">Open sidebar</span>
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Marketing Mobile Menu - Show on marketing pages */}
      {isMarketingPage && isMarketingMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 bg-background border-b border-border z-40 animate-in fade-in slide-in-from-top-2">
          <div className="container py-4">
            <div className="space-y-4">
              {marketingMobileItems.map((item, index) => (
                <div key={index} className="px-4">
                  {item.isAnchor ? (
                    <a
                      href={item.path}
                      className="flex items-center py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAnchorClick(e, item.path);
                        toggleMarketingMenu();
                      }}
                    >
                      {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="flex items-center py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                      onClick={toggleMarketingMenu}
                    >
                      {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
